import { Slot } from '@wordpress/components';
import QuoteHeader from './QuoteDetails/QuoteHeader';
import ProductsTable from './QuoteDetails/ProductsTable';
import ShippingDetails from './QuoteDetails/ShippingDetails';
import AdditionalMessages from './QuoteDetails/AdditionalMessages';
import CustomerDetails from './QuoteDetails/CustomerDetails';
import { useDispatch, useSelect } from '@wordpress/data';
import { useEffect, useState } from '@wordpress/element';
import QuoteDetailsSkeleton from './QuoteDetails/QuoteDetailsSkeleton';
import QuoteNotice from './QuoteDetails/QuoteNotice';
import { QUOTE_STATUS, VENDOR_QUOTES_STORE } from '../constants';
import { DispatchActions, QuoteSelector } from '../types/quote';
import { QUOTE_DETAILS_SLOTS } from './Common/QuoteDetailsSlot';
import { NotFound } from '@dokan/components';

interface QuoteDetailsProps {
    params: {
        quoteId: string | number;
    };
}

const QuoteDetails = ( { params: { quoteId } }: QuoteDetailsProps ) => {
    const [ quoteUpdated, setQuoteUpdated ] = useState< boolean >( false );
    const dispatch = useDispatch( VENDOR_QUOTES_STORE ) as DispatchActions;
    const { quote: data, isLoading } = useSelect< QuoteSelector >(
        ( select ) => ( {
            quote: select( VENDOR_QUOTES_STORE ).getQuote( quoteId ),
            isLoading: select( VENDOR_QUOTES_STORE ).getLoading(),
        } ),
        [ quoteId ]
    ); // Added quoteId to dependency array

    const refreshQuoteDetails = ( quoteId: string | number ) => {
        if ( ! quoteId ) {
            return;
        }

        try {
            dispatch.invalidateResolution( 'getQuote', [ quoteId ] );
        } catch ( error ) {
            console.error( 'Failed to refresh quote data:', error );
        }
    };

    useEffect( () => {
        refreshQuoteDetails( quoteId );
        // Cleanup on unmount or when quoteId changes.
        return () => refreshQuoteDetails( quoteId );
    }, [ quoteId ] );

    if ( isLoading ) {
        return <QuoteDetailsSkeleton />;
    }

    if ( ! data ) {
        return <NotFound />; // Or some error state component
    }

    return (
        <div className="flex flex-col gap-6 h-fit mt-2">
            { /* Before quote details slot */ }
            <Slot
                name={ QUOTE_DETAILS_SLOTS.BEFORE_QUOTE_DETAILS }
                fillProps={ { quoteId, data } }
            />

            <QuoteNotice quote={ data } />

            { /* After quote details notice slot */ }
            <Slot
                name={ QUOTE_DETAILS_SLOTS.AFTER_QUOTE_NOTICE }
                fillProps={ { quoteId, data } }
            />

            <QuoteHeader quote={ data } quoteUpdated={ quoteUpdated } />

            { /* After quote header details slot */ }
            <Slot
                name={ QUOTE_DETAILS_SLOTS.AFTER_QUOTE_HEADER }
                fillProps={ { quoteId, data } }
            />

            <CustomerDetails customerInfo={ data.customer_info } />

            { /* After quote customer details slot */ }
            <Slot
                name={ QUOTE_DETAILS_SLOTS.AFTER_QUOTE_CUSTOMER_DETAILS }
                fillProps={ { quoteId, data } }
            />

            <ProductsTable
                quote={ data }
                quoteUpdated={ quoteUpdated }
                setQuoteUpdated={ setQuoteUpdated }
            />

            { /* After quote product details slot */ }
            <Slot
                name={ QUOTE_DETAILS_SLOTS.AFTER_PRODUCT_TABLE }
                fillProps={ { quoteId, data } }
            />

            { data.customer_info?.country && (
                <>
                    <ShippingDetails
                        customerInfo={ data.customer_info }
                        expectedDate={ data.expected_date }
                    />

                    { /* After quote shipping details slot */ }
                    <Slot
                        name={ QUOTE_DETAILS_SLOTS.AFTER_SHIPPING_DETAILS }
                        fillProps={ { quoteId, data } }
                    />
                </>
            ) }

            { ( data?.status === QUOTE_STATUS?.PENDING ||
                data.customer_info?.customer_additional_msg ||
                data.store_info?.vendor_additional_msg ) && (
                <>
                    <AdditionalMessages quote={ data } />

                    { /* After quote shipping details slot */ }
                    <Slot
                        name={ QUOTE_DETAILS_SLOTS.AFTER_ADDITIONAL_MESSAGE }
                        fillProps={ { quoteId, data } }
                    />
                </>
            ) }

            { /* After quote details slot */ }
            <Slot
                name={ QUOTE_DETAILS_SLOTS.AFTER_QUOTE_DETAILS }
                fillProps={ { quoteId, data } }
            />
        </div>
    );
};

export default QuoteDetails;
